import { SxProps } from "@mui/system"

import { s } from "./common"
import { fontSizeFactor } from "./constants"

export type Theme = {
  palette: typeof palette
  typography: Record<TI, SxProps>
}

export enum TI {
  new_largeTitle = "new_largeTitle",
  new_footnote = "new_footnote",
  new_title3 = "new_title3",
  new_subheadline = "new_subheadline",
  new_subheadline_bold = "new_subheadline_bold",
  new_medium06tv = "new_medium06tv",
  new_caption1 = "new_caption1",
  new_caption2 = "new_caption2",
  new_untitled1 = "new_untitled1",
  new_untitled2 = "new_untitled2",
  H01tv = "H01tv",
  H02tv = "H02tv",
  H03tv = "H03tv",
  H04tv = "H04tv",
  H05tv = "H05tv",
  H06tv = "H06tv",
  H07tv = "H07tv",
  Subheadline01tv = "Subheadline01tv",
  Subheadline02tv = "Subheadline02tv",
  Subheadline03tv = "Subheadline03tv",
  Subheadline04tv = "Subheadline04tv",
  Subheadline05tv = "Subheadline05tv",
  Subheadline06tv = "Subheadline06tv",
  Subheadline07tv = "Subheadline07tv",
  Subheadline08tv = "Subheadline08tv",
  Subheadline09tv = "Subhead09tv",
  Caption01tv = "Caption01tv",
  Caption02tv = "Caption02tv",
  Caption03tv = "Caption03tv",
  Regular04tv = "Regular04tv",
}

const palette = {
  new: {
    surface: {
      "0": "#DADADA",
      "100": "#BFBFBF",
      "200": "#A0A0A0",
    },
    categoryButton: "#3E669F",
    playerButton: "#262626",
  },
  primary: {
    main: "#151F31",
    variant: "#1A2436",
  },
  secondary: {
    main: "#0A0F17",
    variant: "#232C3D",
    "70": "#0A0F17",
  },
  tertiary: {
    main: "#111A28",
    accentYellow: "#FFED00",
    error: "#FE7F49",
    justNotes: "#BC31EA",
  },
  white: {
    main: "#FFFFFF",
    greyLight: "#C4C4C4",
    grey: "#929292",
    "10": "rgba(255,255,255,0.1)",
    "20": "rgba(255,255,255,0.2)",
    "30": "rgba(255,255,255,0.3)",
    "40": "rgba(255,255,255,0.4)",
    "50": "rgba(255,255,255,0.5)",
    "55": "rgba(255,255,255,0.55)",
    "70": "rgba(255,255,255,0.7)",
  },
  black: {
    main: "#000000",
    buttonBlue: "#303949",
    accentRed: "#DF1142",
  },
} as const

// new_ is a prefix for the fact that the design team just tossed the entire typography set and created a new one
export const theme: Theme = {
  palette,
  typography: {
    [TI.new_largeTitle]: {
      fontWeight: 600,
      fontSize: s("90px", false, fontSizeFactor),
      lineHeight: s("112px", false, fontSizeFactor),
      letterSpacing: 0,
    },
    [TI.new_footnote]: {
      fontWeight: 600,
      fontSize: s("22px", false, fontSizeFactor),
      lineHeight: s("30px", false, fontSizeFactor),
      letterSpacing: 0,
    },
    [TI.new_title3]: {
      fontWeight: 600,
      fontSize: s("42px", false, fontSizeFactor),
      lineHeight: s("52px", false, fontSizeFactor),
      letterSpacing: 0,
    },
    [TI.new_caption1]: {
      fontWeight: 600,
      fontSize: s("28px", false, fontSizeFactor),
      lineHeight: s("32px", false, fontSizeFactor),
      letterSpacing: 0,
    },
    [TI.new_caption2]: {
      fontWeight: 500,
      fontSize: s("24px", false, fontSizeFactor),
      lineHeight: s("30px", false, fontSizeFactor),
      letterSpacing: 0,
    },
    [TI.new_subheadline]: {
      fontWeight: 500,
      fontSize: s("28px", false, fontSizeFactor),
      lineHeight: s("36px", false, fontSizeFactor),
      letterSpacing: 0,
    },
    [TI.new_subheadline_bold]: {
      fontWeight: 600,
      fontSize: s("28px", false, fontSizeFactor),
      lineHeight: s("36px", false, fontSizeFactor),
      letterSpacing: 0,
    },
    [TI.new_medium06tv]: {
      fontWeight: 600,
      fontSize: s("24px", false, fontSizeFactor),
      lineHeight: s("34px", false, fontSizeFactor),
      letterSpacing: s("0.25px"),
    },
    // WeeklyTop10 ranking
    [TI.new_untitled1]: {
      fontWeight: 600,
      fontSize: s("164px", false, fontSizeFactor),
      lineHeight: s("135px", false, fontSizeFactor),
      letterSpacing: s("-1px"),
      WebkitTextStroke: s("4px"),
      WebkitTextFillColor: "transparent",
    },
    [TI.new_untitled2]: {
      fontWeight: 600,
      fontSize: s("72px", false, fontSizeFactor),
      lineHeight: s("62px", false, fontSizeFactor),
      letterSpacing: s("-1px"),
      WebkitTextStroke: s("2px"),
      WebkitTextFillColor: "transparent",
    },
    [TI.H01tv]: {
      fontWeight: 600,
      fontSize: s("72px", false, fontSizeFactor),
      lineHeight: s("80px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.H02tv]: {
      fontWeight: 200,
      fontSize: s("72px", false, fontSizeFactor),
      lineHeight: s("80px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.H03tv]: {
      fontWeight: 600,
      fontSize: s("64px", false, fontSizeFactor),
      lineHeight: s("72px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.H04tv]: {
      fontWeight: 200,
      fontSize: s("64px", false, fontSizeFactor),
      lineHeight: s("72px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.H05tv]: {
      fontWeight: 600,
      fontSize: s("48px", false, fontSizeFactor),
      lineHeight: s("58px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.H06tv]: {
      fontWeight: 400,
      fontSize: s("48px", false, fontSizeFactor),
      lineHeight: s("58px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.H07tv]: {
      fontWeight: 200,
      fontSize: s("48px", false, fontSizeFactor),
      lineHeight: s("58px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.Subheadline01tv]: {
      fontWeight: 600,
      fontSize: s("34px", false, fontSizeFactor),
      lineHeight: s("46px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.Subheadline02tv]: {
      fontWeight: 400,
      fontSize: s("34px", false, fontSizeFactor),
      lineHeight: s("46px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.Subheadline03tv]: {
      fontWeight: 200,
      fontSize: s("34px", false, fontSizeFactor),
      lineHeight: s("46px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.Subheadline04tv]: {
      fontWeight: 600,
      fontSize: s("30px", false, fontSizeFactor),
      lineHeight: s("40px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.Subheadline05tv]: {
      fontWeight: 400,
      fontSize: s("30px", false, fontSizeFactor),
      lineHeight: s("40px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.Subheadline06tv]: {
      fontWeight: 200,
      fontSize: s("30px", false, fontSizeFactor),
      lineHeight: s("40px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.Subheadline07tv]: {
      fontWeight: 600,
      fontSize: s("24px", false, fontSizeFactor),
      lineHeight: s("34px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.Subheadline08tv]: {
      fontWeight: 400,
      fontSize: s("24px", false, fontSizeFactor),
      lineHeight: s("34px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.Subheadline09tv]: {
      fontWeight: 200,
      fontSize: s("24px", false, fontSizeFactor),
      lineHeight: s("34px", false, fontSizeFactor),
      letterSpacing: s("0.25px", false, fontSizeFactor),
    },
    [TI.Caption01tv]: {
      fontWeight: 600,
      fontSize: s("16px", false, fontSizeFactor),
      lineHeight: s("26px", false, fontSizeFactor),
      letterSpacing: 0,
    },
    [TI.Caption02tv]: {
      fontWeight: 400,
      fontSize: s("16px", false, fontSizeFactor),
      lineHeight: s("26px", false, fontSizeFactor),
      letterSpacing: 0,
    },
    [TI.Caption03tv]: {
      fontWeight: 400,
      fontSize: s("16px", false, fontSizeFactor),
      lineHeight: s("26px", false, fontSizeFactor),
      letterSpacing: 0,
      textTransform: "uppercase",
    },
    [TI.Regular04tv]: {
      fontWeight: 400,
      fontSize: s("36px", false, fontSizeFactor),
      lineHeight: s("48px", false, fontSizeFactor),
      letterSpacing: 0.25,
    },
  },
} as const
